import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Wallet, Globe, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";

interface BalanceResult {
  network: string;
  address: string;
  nativeBalance: number;
  nativeCurrency: string;
  tokens: Array<{ symbol: string; balance: number }>;
}

interface AggregatedBalance {
  symbol: string;
  totalBalance: number;
  addressCount: number;
}

const CHAIN_MAP: Record<string, string> = {
  "Ethereum (ERC20)": "0x1",
  "BSC (BEP20)": "0x38",
  "Polygon": "0x89",
  "Arbitrum": "0xa4b1",
  "Avalanche C-Chain": "0xa86a",
  "Optimism": "0xa",
  "Base": "0x2105",
  "Solana": "solana",
};

const NATIVE_SYMBOLS: Record<string, string> = {
  "Ethereum (ERC20)": "ETH",
  "BSC (BEP20)": "BNB",
  "Polygon": "MATIC",
  "Arbitrum": "ETH",
  "Avalanche C-Chain": "AVAX",
  "Optimism": "ETH",
  "Base": "ETH",
  "Solana": "SOL",
};

const AdminBlockchainBalances = () => {
  const [loading, setLoading] = useState(false);
  const [aggregated, setAggregated] = useState<AggregatedBalance[]>([]);
  const [networkDetails, setNetworkDetails] = useState<Record<string, BalanceResult[]>>({});
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchBalances = async () => {
    setLoading(true);
    try {
      // Get all unique addresses grouped by network
      const { data: addresses, error } = await supabase
        .from("user_crypto_addresses")
        .select("network, address");

      if (error) throw error;
      if (!addresses || addresses.length === 0) {
        toast.info("لا توجد عناوين مسجلة");
        setLoading(false);
        return;
      }

      // Deduplicate addresses per network
      const uniqueByNetwork: Record<string, Set<string>> = {};
      for (const a of addresses) {
        if (!uniqueByNetwork[a.network]) uniqueByNetwork[a.network] = new Set();
        uniqueByNetwork[a.network].add(a.address);
      }

      const allResults: BalanceResult[] = [];
      const balanceMap: Record<string, { total: number; count: number }> = {};

      // Check balances for each unique address (batch by network)
      for (const [network, addrs] of Object.entries(uniqueByNetwork)) {
        const chainId = CHAIN_MAP[network];
        if (!chainId) continue;

        for (const address of addrs) {
          try {
            const { data, error: fnErr } = await supabase.functions.invoke("check-balance", {
              body: { address, chain: chainId },
            });

            if (fnErr || !data) continue;

            const result: BalanceResult = {
              network,
              address,
              nativeBalance: data.nativeBalance || 0,
              nativeCurrency: NATIVE_SYMBOLS[network] || data.nativeCurrency,
              tokens: data.tokens || [],
            };
            allResults.push(result);

            // Aggregate native
            if (result.nativeBalance > 0) {
              const sym = result.nativeCurrency;
              if (!balanceMap[sym]) balanceMap[sym] = { total: 0, count: 0 };
              balanceMap[sym].total += result.nativeBalance;
              balanceMap[sym].count++;
            }

            // Aggregate tokens
            for (const token of result.tokens) {
              if (token.balance > 0) {
                if (!balanceMap[token.symbol]) balanceMap[token.symbol] = { total: 0, count: 0 };
                balanceMap[token.symbol].total += token.balance;
                balanceMap[token.symbol].count++;
              }
            }
          } catch (e) {
            console.error(`Error checking ${network}/${address}:`, e);
          }
        }
      }

      // Build aggregated list
      const agg = Object.entries(balanceMap)
        .map(([symbol, { total, count }]) => ({
          symbol,
          totalBalance: total,
          addressCount: count,
        }))
        .sort((a, b) => b.totalBalance - a.totalBalance);

      // Group results by network
      const byNetwork: Record<string, BalanceResult[]> = {};
      for (const r of allResults) {
        if (!byNetwork[r.network]) byNetwork[r.network] = [];
        byNetwork[r.network].push(r);
      }

      setAggregated(agg);
      setNetworkDetails(byNetwork);
      setLastUpdated(new Date().toLocaleTimeString("ar-EG"));
      toast.success(`تم فحص ${allResults.length} عنوان بنجاح`);
    } catch (e) {
      console.error(e);
      toast.error("حدث خطأ أثناء جلب الأرصدة");
    } finally {
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState(false);

  const syncBalances = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-balances");
      if (error) throw error;
      toast.success(`تمت المزامنة: ${data?.walletsUpdated || 0} محفظة تم تحديثها لـ ${data?.usersProcessed || 0} مستخدم`);
    } catch (e) {
      console.error(e);
      toast.error("حدث خطأ أثناء مزامنة الأرصدة");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="glass border-border col-span-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            أرصدة البلوكتشين الحقيقية
          </CardTitle>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">آخر تحديث: {lastUpdated}</span>
          )}
          <Button
            size="sm"
            variant="default"
            onClick={syncBalances}
            disabled={syncing}
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDownUp className="h-4 w-4" />
            )}
            <span className="mr-1">{syncing ? "جاري المزامنة..." : "مزامنة أرصدة المستخدمين"}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchBalances}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="mr-1">{loading ? "جاري الفحص..." : "فحص الأرصدة"}</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {aggregated.length > 0 ? (
          <div className="space-y-4">
            {/* Aggregated totals */}
            <div className="flex flex-wrap gap-3">
              {aggregated.map((b) => (
                <div
                  key={b.symbol}
                  className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2"
                >
                  <Wallet className="h-4 w-4 text-primary" />
                  <span className="font-bold text-foreground">{b.symbol}</span>
                  <span className="text-primary font-mono text-sm">
                    {b.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({b.addressCount} عنوان)
                  </span>
                </div>
              ))}
            </div>

            {/* Network breakdown */}
            <div className="space-y-3 mt-4">
              {Object.entries(networkDetails).map(([network, results]) => {
                const nonZero = results.filter(
                  (r) => r.nativeBalance > 0 || r.tokens.some((t) => t.balance > 0)
                );
                if (nonZero.length === 0) return null;

                return (
                  <details key={network} className="group">
                    <summary className="cursor-pointer text-sm font-medium text-foreground hover:text-primary transition-colors">
                      {network} — {nonZero.length} عنوان برصيد
                    </summary>
                    <div className="mt-2 space-y-1 pr-4">
                      {nonZero.map((r) => (
                        <div
                          key={r.address}
                          className="text-xs text-muted-foreground font-mono bg-muted/30 rounded px-2 py-1 flex flex-wrap gap-2"
                        >
                          <span className="truncate max-w-[200px]">{r.address}</span>
                          {r.nativeBalance > 0 && (
                            <span className="text-foreground">
                              {r.nativeCurrency}: {r.nativeBalance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                            </span>
                          )}
                          {r.tokens
                            .filter((t) => t.balance > 0)
                            .map((t) => (
                              <span key={t.symbol} className="text-primary">
                                {t.symbol}: {t.balance.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                              </span>
                            ))}
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {loading ? "جاري فحص العناوين على البلوكتشين..." : "اضغط 'فحص الأرصدة' لعرض الأرصدة الحقيقية من البلوكتشين"}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBlockchainBalances;
